import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import {
  Table,
  Tag,
  Button,
  Drawer,
  Descriptions,
  Divider,
  Steps,
  DatePicker,
  Select,
  Space,
  Spin
} from 'antd'
import {
  ShoppingOutlined,
  DollarOutlined,
  UserOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  MailOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import api from '../utils/api'
import { formatTz, now as tzNow } from '../utils/timezone'

const PageContainer = styled.div`
  min-height: 100vh;
`

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`

const DrawerSection = styled.div`
  margin-bottom: 24px;
`

const OrderStatus = styled.div`
  background: #f6f6f6;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 24px;
`

const ProductCard = styled.div`
  background: #ffffff;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`

const ProductInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const ProductName = styled.div`
  font-weight: 500;
`

const ProductQuantity = styled.div`
  color: #666;
  font-size: 14px;
`

const ProductPrice = styled.div`
  color: #f5222d;
  font-weight: 500;
`

const TotalSection = styled.div`
  background: #f6f6f6;
  padding: 16px;
  border-radius: 8px;
  margin-top: 16px;

  .total-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;

    &.final-total {
      margin-top: 8px;
      padding-top: 8px;
      border-top: 1px dashed #d9d9d9;
      font-weight: bold;
    }
  }
`

const CustomerInfo = styled.div`
  background: #fff;
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 24px;

  .info-item {
    display: flex;
    align-items: center;
    margin-bottom: 8px;

    .icon {
      margin-right: 8px;
      color: #1890ff;
    }
  }
`

const { RangePicker } = DatePicker

const OrdersPage = () => {
  const [visible, setVisible] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [loading, setLoading] = useState(false)
  const [orders, setOrders] = useState([])
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  })
  const [filters, setFilters] = useState({
    status: undefined,
    dateRange: [tzNow().startOf('year'), tzNow().endOf('year')]
  })

  const fetchOrders = async (params = {}) => {
    setLoading(true)
    try {
      const response = await api.get('/api/store/orders', {
        params: {
          page: params.current || pagination.current,
          pageSize: params.pageSize || pagination.pageSize,
          status: filters.status,
          startDate: filters.dateRange[0]?.format('YYYY-MM-DD'),
          endDate: filters.dateRange[1]?.format('YYYY-MM-DD')
        }
      })

      const { data } = response
      setOrders(data.data.items)
      setPagination({
        current: data.data.page,
        pageSize: data.data.pageSize,
        total: data.data.total
      })
    } catch (error) {
      console.error('獲取訂單失敗:', error)
    }
    setLoading(false)
  }

  useEffect(() => {
    fetchOrders()
  }, [filters])

  const columns = [
    {
      title: '訂單編號',
      dataIndex: 'orderId',
      key: 'orderId'
    },
    {
      title: '顧客',
      dataIndex: ['customerInfo', 'email'],
      key: 'customer'
    },
    {
      title: '訂單狀態',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag
          color={
            status === 'pending'
              ? 'warning'
              : status === 'paid'
              ? 'processing'
              : status === 'completed'
              ? 'success'
              : 'default'
          }
        >
          {status === 'pending'
            ? '待付款'
            : status === 'paid'
            ? '已付款'
            : status === 'completed'
            ? '已完成'
            : status}
        </Tag>
      )
    },
    {
      title: '總金額',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => `NT$${amount.toLocaleString()}`
    },
    {
      title: '建立日期',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm')
    },
    {
      title: '付款方式',
      dataIndex: 'paymentType',
      key: 'paymentType',
      render: (type) => (type === 'newebpay' ? '藍新金流' : type)
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Button type='link' onClick={() => showDrawer(record)}>
          查看詳情
        </Button>
      )
    }
  ]

  const showDrawer = (order) => {
    setSelectedOrder(order)
    setVisible(true)
  }

  const onClose = () => {
    setVisible(false)
    setSelectedOrder(null)
  }

  const headerContent = (
    <HeaderSection>
      <h1>訂單管理</h1>
      <Space size='middle'>
        <Select
          style={{ width: 120 }}
          placeholder='訂單狀態'
          allowClear
          onChange={(value) =>
            setFilters((prev) => ({ ...prev, status: value }))
          }
        >
          <Select.Option value='pending'>待付款</Select.Option>
          <Select.Option value='paid'>已付款</Select.Option>
          <Select.Option value='shipped'>已發貨</Select.Option>
          <Select.Option value='completed'>已完成</Select.Option>
        </Select>
        <RangePicker
          value={filters.dateRange}
          onChange={(dates) =>
            setFilters((prev) => ({ ...prev, dateRange: dates }))
          }
        />
      </Space>
    </HeaderSection>
  )

  const tableProps = {
    columns,
    dataSource: orders,
    loading,
    pagination: {
      ...pagination,
      onChange: (page, pageSize) => {
        fetchOrders({ current: page, pageSize })
      }
    }
  }

  return (
    <PageContainer>
      {headerContent}
      <Table {...tableProps} />

      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <ShoppingOutlined style={{ marginRight: 8, color: '#1890ff' }} />
            訂單詳情 #{selectedOrder?.orderId}
          </div>
        }
        placement='right'
        onClose={onClose}
        visible={visible}
        width={500}
      >
        {selectedOrder && (
          <>
            <OrderStatus>
              <Steps
                current={
                  selectedOrder.status === 'completed'
                    ? 2
                    : selectedOrder.status === 'paid'
                    ? 1
                    : 0
                }
                items={[
                  {
                    title: '待付款',
                    description: formatTz(
                      selectedOrder.createdAt,
                      'YYYY-MM-DD HH:mm'
                    )
                  },
                  {
                    title: '已付款',
                    description: selectedOrder.paymentInfo?.payTime
                      ? dayjs(selectedOrder.paymentInfo.payTime).format(
                          'YYYY-MM-DD HH:mm'
                        )
                      : ''
                  },
                  {
                    title: '已完成',
                    description:
                      selectedOrder.status === 'completed'
                        ? formatTz(
                            selectedOrder.updatedAt,
                            'YYYY-MM-DD HH:mm'
                          )
                        : ''
                  }
                ]}
              />
            </OrderStatus>

            <DrawerSection>
              <h3>
                <UserOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                顧客資訊
              </h3>
              <CustomerInfo>
                <div className='info-item'>
                  <MailOutlined className='icon' />
                  {selectedOrder.customerInfo?.email || '未提供'}
                </div>
              </CustomerInfo>
            </DrawerSection>

            <DrawerSection>
              <h3>
                <ShoppingOutlined
                  style={{ marginRight: 8, color: '#1890ff' }}
                />
                商品明細
              </h3>
              {selectedOrder.orderDetails.products.map((item) => (
                <ProductCard key={item._id}>
                  <ProductInfo>
                    <div>
                      <ProductName>{item.title}</ProductName>
                      <ProductQuantity>數量：{item.quantity}</ProductQuantity>
                    </div>
                    <ProductPrice>
                      NT$ {item.price.toLocaleString()}
                    </ProductPrice>
                  </ProductInfo>
                </ProductCard>
              ))}

              <TotalSection>
                <div className='total-row'>
                  <span>商品小計</span>
                  <span>
                    NT${' '}
                    {(
                      selectedOrder.amount -
                      selectedOrder.orderDetails.shippingFee
                    ).toLocaleString()}
                  </span>
                </div>
                <div className='total-row'>
                  <span>運費</span>
                  <span>NT$ {selectedOrder.orderDetails.shippingFee}</span>
                </div>
                <div className='total-row final-total'>
                  <span>訂單總計</span>
                  <span>NT$ {selectedOrder.amount.toLocaleString()}</span>
                </div>
              </TotalSection>
            </DrawerSection>

            <DrawerSection>
              <h3>
                <DollarOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                付款資訊
              </h3>
              <Descriptions bordered size='small' column={1}>
                <Descriptions.Item label='付款方式'>
                  {selectedOrder.paymentType === 'newebpay'
                    ? '藍新金流'
                    : selectedOrder.paymentType}
                </Descriptions.Item>
                {selectedOrder.paymentInfo?.tradeNo && (
                  <Descriptions.Item label='交易編號'>
                    {selectedOrder.paymentInfo.tradeNo}
                  </Descriptions.Item>
                )}
                {selectedOrder.paymentInfo?.payTime && (
                  <Descriptions.Item label='付款時間'>
                    {dayjs(selectedOrder.paymentInfo.payTime).format(
                      'YYYY-MM-DD HH:mm:ss'
                    )}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </DrawerSection>
          </>
        )}
      </Drawer>
    </PageContainer>
  )
}

export default OrdersPage
